<div class="modal fade show d-block" tabindex="-1">
  <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-sm-down modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-shield-check me-1"></i>Privacy Policy and Terms of Agreement
        </h5>
        <button type="button" class="btn-close" (click)="close.emit()"></button>
      </div>

      <div class="modal-body">
        <ul class="nav nav-pills nav-fill mb-3">
          <li class="nav-item">
            <button type="button" class="nav-link" [class.active]="section() === 'privacy'"
                    (click)="section.set('privacy')">
              Privacy Policy
            </button>
          </li>
          <li class="nav-item">
            <button type="button" class="nav-link" [class.active]="section() === 'terms'"
                    (click)="section.set('terms')">
              Terms of Agreement
            </button>
          </li>
        </ul>

        <p class="text-body-secondary small">Last updated: {{ lastUpdated }}</p>

        @if (section() === 'privacy') {
          <h6>1. Who we are</h6>
          <p>
            PassKing is a password and personal-document manager operated by Askenish Technology
            LTD (&ldquo;we&rdquo;, &ldquo;us&rdquo;). This policy explains what information the
            service handles and how.
          </p>

          <h6>2. Your vault is zero-knowledge</h6>
          <p>
            Everything you store in your vault &mdash; passwords, IDs, passports, cards, health
            insurance details, notes, and even your tag names &mdash; is encrypted <em>on your
            device</em> with AES&#8209;256&#8209;GCM before it is sent to us. The encryption key is
            derived from your master password, which never leaves your device and is never
            transmitted or stored. Our servers hold only ciphertext:
            <strong>we cannot read, decrypt, or recover the contents of your vault.</strong>
            If you forget your master password, your vault contents are permanently unrecoverable.
          </p>
          <p>
            Your optional master password hint is stored as you type it and is readable by us, so
            never include the password itself or other secrets in the hint.
          </p>

          <h6>3. Account information we store</h6>
          <p>We store the minimum needed to run your account:</p>
          <ul>
            <li>Your name, email address, and optional public &#64;username.</li>
            <li>A one-way Argon2id hash of your login password (never the password itself, and
                never your master password).</li>
            <li>Short-lived email verification codes, session refresh tokens, trusted-device
                records, and sign-in failure counters used for lockout protection.</li>
          </ul>
          <p>
            IP addresses are processed transiently to rate-limit abusive traffic; we do not build
            profiles from them.
          </p>

          <h6>4. Friends and messages</h6>
          <p>
            If you claim a username, other users can find it to send you a friend request.
            Direct messages between friends are protected in transit (HTTPS) and stored on our
            servers so both participants can read their history, but they are
            <strong>not end-to-end encrypted</strong>. When you use the share feature to send a
            vault entry to a friend, a decrypted copy of that entry travels inside the message
            &mdash; only share credentials with people you trust, and treat sharing as taking that
            entry outside your private vault.
          </p>

          <h6>5. Document scanning</h6>
          <p>
            When you scan or upload a document (ID, passport, card, or insurance card) to prefill
            an entry, the image or PDF is forwarded to Microsoft Azure AI Content Understanding
            for analysis and the recognized fields are returned to your device. We do not store
            the uploaded document; the extracted values are encrypted on your device the moment
            you save the entry. Microsoft processes the document under its own service terms.
            Scanning is optional &mdash; you can always type fields in manually.
          </p>

          <h6>6. Service providers</h6>
          <p>
            We use Microsoft Azure to host the service and analyze scanned documents, and Twilio
            SendGrid to deliver account emails (such as verification codes). We do not sell your
            data, show advertising, or use third-party analytics or tracking.
          </p>

          <h6>7. Data on your device</h6>
          <p>
            The app keeps session tokens, your theme preference, and (as an installable web app)
            a cached copy of the application itself in your browser&rsquo;s local storage. Vault
            data is decrypted only in memory while you are signed in.
          </p>

          <h6>8. Retention and your rights</h6>
          <p>
            We keep your data while your account is active. Deleting a vault entry, tag, or
            message removes it from our systems. You can update your name, email, username, and
            passwords from your profile at any time. To request account deletion or a copy of
            the account data we hold, contact us at
            <a href="mailto:support&#64;askenish.com">support&#64;askenish.com</a>.
          </p>

          <h6>9. Changes to this policy</h6>
          <p>
            If we materially change how we handle your data, we will update this policy and the
            &ldquo;last updated&rdquo; date above.
          </p>
        } @else {
          <h6>1. Acceptance</h6>
          <p>
            By creating an account or using PassKing you agree to these terms with Askenish
            Technology LTD. If you do not agree, do not use the service.
          </p>

          <h6>2. The service</h6>
          <p>
            PassKing lets you store encrypted passwords and personal documents, optionally
            prefill entries by scanning documents, and connect with friends to message and share
            entries. Features may change or be discontinued as the service evolves.
          </p>

          <h6>3. Your account and passwords</h6>
          <p>
            You are responsible for the accuracy of your account information and for everything
            done under your account. Keep your login password and master password confidential
            and choose strong, distinct values for each.
          </p>

          <h6>4. Master password &mdash; no recovery</h6>
          <p>
            The service is deliberately designed so that we never possess your master password or
            the keys to your vault. <strong>We cannot reset your master password or recover your
            vault if you lose it.</strong> You accept that losing your master password means the
            permanent loss of your encrypted vault contents, and that we are not liable for that
            loss.
          </p>

          <h6>5. Acceptable use</h6>
          <p>You agree not to:</p>
          <ul>
            <li>Store, scan, or share credentials or documents you are not authorized to hold.</li>
            <li>Use the service for anything unlawful, or to harass or defraud other users.</li>
            <li>Attempt to access other users&rsquo; accounts or data, probe or disrupt the
                service, or circumvent its security and rate-limiting measures.</li>
          </ul>

          <h6>6. Sharing with friends</h6>
          <p>
            Sharing a vault entry sends a decrypted copy to your chosen recipient, who can keep,
            import, and further use it. You share at your own risk; we are not responsible for
            what recipients do with information you send them.
          </p>

          <h6>7. Service &ldquo;as is&rdquo;</h6>
          <p>
            The service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without
            warranties of any kind, express or implied, including fitness for a particular
            purpose and uninterrupted availability. Document scanning is an automated
            convenience: always review extracted values before saving.
          </p>

          <h6>8. Limitation of liability</h6>
          <p>
            To the maximum extent permitted by law, Askenish Technology LTD is not liable for
            indirect, incidental, special, or consequential damages, or for loss of data arising
            from a forgotten master password, credential sharing you initiated, or events beyond
            our reasonable control.
          </p>

          <h6>9. Termination</h6>
          <p>
            You may stop using the service and request account deletion at any time. We may
            suspend or terminate accounts that violate these terms or threaten the security of
            the service.
          </p>

          <h6>10. Changes and governing law</h6>
          <p>
            We may update these terms; continued use after an update constitutes acceptance.
            These terms are governed by the laws of the jurisdiction in which Askenish Technology
            LTD is registered. Questions? Contact
            <a href="mailto:support&#64;askenish.com">support&#64;askenish.com</a>.
          </p>
        }
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-primary" (click)="close.emit()">Close</button>
      </div>
    </div>
  </div>
</div>
<div class="modal-backdrop fade show"></div>
